import {
  Archive,
  Asterisk,
  AtSign,
  Blocks,
  BookUser,
  Building,
  CircleDashed,
  CircleStar,
  CircleUserRound,
  Globe,
  Hash,
  Headset,
  Heart,
  Package,
  ScanSearch,
  Tag as TagIcon,
  Workflow,
} from 'lucide-react';
import { useMemo } from 'react';

import { type MenuItemProps, MenuItemType } from '@/components/Menu/types';
import { CHART_FIELD } from '@/entities/chart/model/constants';
import {
  HashtagDatasetTagInput,
  KeywordDatasetTagInput,
  MentionDatasetTagInput,
} from '@/features/(charts)/chart-controller/ui/components/chart-dimension-select/dataset-tag-input';
import { getChartFieldLabel } from '@/features/(charts)/chart-renderer/model/chart-maps';
import useCompanyStore, { COMPANY_PAGE_SIZE } from '@/features/Account/store';
import IconTextProperty from '@/features/Commons/Label/IconTextProperty';
import SentimentBadge from '@/features/Feedback/field/Sentiment/SentimentBadge';
import sourceUtils from '@/features/Feedback/field/Source/source.utils';
import Label from '@/features/Feedback/field/Tag/Label';
import useProductStore from '@/features/Product/store/ProductStore';
import useCategoryMap from '@/hooks/domain/useCategoryMap';
import useCompanyAttributes from '@/hooks/domain/useCompanyAttributes';
import service from '@/services/index';
import { CUSTOMER_PAGE_SIZE } from '@/stores/CustomerStore';
import useLocaleStore, {
  useFeedbackLanguageList,
  useFeedbackRegionList,
} from '@/stores/LocaleStore';
import useTagStore from '@/stores/TagStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { OrderedCategoryList } from '@/types/categoryConst';
import {
  adPredictionList,
  ageGroupList,
  FEEDBACK_STATUS,
  genderList,
  RACE,
  sentimentMap,
  SOURCE_STATUS,
} from '@/types/feedbackConst';
import {
  ACCOUNT_COLUMNS,
  CONTACT_COLUMNS,
  DEMOGRAPHIC_COLUMNS,
  FEEDBACK_COLUMNS,
  PRODUCT_COLUMNS,
  SCORE_COLUMNS,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { orderedSentiment } from '@/types/sentimentConst';
import feedbackUtils from '@/utils/feedback';
import { getPaginationQueries } from '@/utils/pagination';
import { getTagFilterTree } from '@/utils/tag';
import { buildQuery, createOptionsFromValues } from '@/utils/util';

const useFeedbackFieldConfig = (): FilterOptionT[] => {
  const currentCategoryMap = useCategoryMap();
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );
  const tagMap = useTagStore((state) => state.tagMap);
  const languageList = useFeedbackLanguageList();

  const feedbackFilterConfig: FilterOptionT[] = useMemo(() => {
    let configs: FilterOptionT[] = [
      {
        columnKey: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
        columnName: 'Taxonomy',
        searchable: true,
      },
      {
        columnKey: FEEDBACK_COLUMNS.TAG,
        columnName: 'Tag',
        searchable: true,
        filterable: true,
        isTreeType: true,
        isMultiValueField: true,
        filterList: getTagFilterTree(tagMap),
      },
      {
        columnKey: FEEDBACK_COLUMNS.CATEGORY,
        columnName: 'Category',
        isMultiValueField: true,
        searchable: true,
        filterable: true,
        filterList: OrderedCategoryList.map((id) => {
          const category = currentCategoryMap[id];
          return {
            columnKey: FEEDBACK_COLUMNS.CATEGORY,
            filterKey: category.id,
            filterName: category.name,
            filterOptionNode: (
              <Label
                key={category.id}
                text={category.name}
                customColor={category.color}
                themeColor={0}
                isSelected={false}
                withoutBorder
                withoutHover
                style={{ padding: 0 }}
              />
            ),
          };
        }),
      },
      {
        columnKey: SENTIMENT_COLUMNS.KEY_SENTIMENT,
        columnName: 'Key sentiment',
        filterable: true,
        searchable: true,
        filterList: orderedSentiment.map((key) => {
          return {
            columnKey: SENTIMENT_COLUMNS.KEY_SENTIMENT,
            filterKey: key,
            filterName: sentimentMap[key].value,
            filterOptionNode: <SentimentBadge type={key} />,
          };
        }),
      },
      {
        columnKey: SENTIMENT_COLUMNS.SENTIMENT_DETAIL,
        columnName: 'Sentiments',
        filterable: true,
        searchable: true,
        filterList: orderedSentiment.map((key) => {
          return {
            columnKey: SENTIMENT_COLUMNS.SENTIMENT_DETAIL,
            filterKey: key,
            filterName: sentimentMap[key].value,
            filterOptionNode: <SentimentBadge type={key} />,
          };
        }),
      },
      {
        columnKey: SOURCE_COLUMNS.SOURCE_TYPE,
        columnName: 'Source',
        filterable: true,
        searchable: true,
        validate: {
          title: 'Filter Change Notice',
          message:
            'If you select another filter item, the previously chosen items will be removed.',
          filterValidator: sourceUtils.validateNewSourceFilter,
        },
      },

      {
        columnKey: SOURCE_COLUMNS.DATA_ORIGIN,
        columnName: 'Data Origin',
        filterable: true,
        searchable: true,
        filterList: workspaceFieldOptions.data_origin_values.map((data) => ({
          columnKey: SOURCE_COLUMNS.DATA_ORIGIN,
          filterKey: data,
          filterName: data,
          filterOptionNode: data,
        })),
      },
      {
        columnKey: FEEDBACK_COLUMNS.KEYWORD,
        columnName: 'Keyword',
        searchable: true,
        filterable: false,
        isMultiValueField: true,
        filterList: [],
        ChartFilterItem: KeywordDatasetTagInput,
      },
      {
        columnKey: FEEDBACK_COLUMNS.HASHTAG,
        columnName: 'Hashtag',
        searchable: true,
        filterable: false,
        isMultiValueField: true,
        filterList: [],
        ChartFilterItem: HashtagDatasetTagInput,
      },
      {
        columnKey: FEEDBACK_COLUMNS.MENTION,
        columnName: 'Mention',
        searchable: true,
        filterable: false,
        isMultiValueField: true,
        filterList: [],
        ChartFilterItem: MentionDatasetTagInput,
      },
      {
        columnKey: FEEDBACK_COLUMNS.REMARKS,
        columnName: 'Remarks',
        filterable: true,
        searchable: true,
        isMultiValueField: true,
        filterList: [],
        asyncData: {
          asyncSearch: true,
          fetchList: async (prev, currPage, targetPage, searchValue) => {
            if (!searchValue)
              return {
                list: [],
                hasNextPage: false,
                page: 0,
              };
            const res = await service.feedback.searchRemarks(searchValue);
            if (!res.remarks)
              return {
                list: [],
                hasNextPage: false,
                page: 0,
              };
            const list = res.remarks.map((remark) => ({
              columnKey: FEEDBACK_COLUMNS.REMARKS,
              filterKey: remark,
              filterName: remark,
              filterOptionNode: remark,
              paginate_token: remark,
            }));
            return {
              list,
              hasNextPage: false,
              page: 0,
            };
          },
        },
      },
      {
        columnKey: FEEDBACK_COLUMNS.AD_PREDICTION,
        columnName: 'Ad prediction',
        filterable: true,
        searchable: false,
        filterList: Array.from(adPredictionList.entries()).map(
          ([key, value]) => ({
            columnKey: FEEDBACK_COLUMNS.AD_PREDICTION,
            filterKey: key,
            filterName: value,
            filterOptionNode: value,
          }),
        ),
      },
      {
        columnKey: SCORE_COLUMNS.RATING_SCORE,
        columnName: 'Rating Score',
        filterable: true,
        searchable: true,
        filterList: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => ({
          columnKey: SCORE_COLUMNS.RATING_SCORE,
          filterKey: num,
          filterName: String(num),
          filterOptionNode: String(num),
        })),
      },
      {
        columnKey: SCORE_COLUMNS.NPS_SCORE,
        columnName: 'NPS Score',
        filterable: true,
        searchable: true,
        filterList: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => ({
          columnKey: SCORE_COLUMNS.NPS_SCORE,
          filterKey: num,
          filterName: String(num),
          filterOptionNode: String(num),
        })),
      },
      {
        columnKey: SCORE_COLUMNS.CES_SCORE,
        columnName: 'CES Score',
        filterable: true,
        searchable: true,
        filterList: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => ({
          columnKey: SCORE_COLUMNS.CES_SCORE,
          filterKey: num,
          filterName: String(num),
          filterOptionNode: String(num),
        })),
      },
      {
        columnKey: SCORE_COLUMNS.CSAT_SCORE,
        columnName: 'CSAT Score',
        filterable: true,
        searchable: true,
        filterList: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => ({
          columnKey: SCORE_COLUMNS.CSAT_SCORE,
          filterKey: num,
          filterName: String(num),
          filterOptionNode: String(num),
        })),
      },
      {
        columnKey: FEEDBACK_COLUMNS.TICKET_STATUS,
        columnName: 'Ticket status',
        filterable: true,
        filterList: [
          SOURCE_STATUS.CLOSE,
          SOURCE_STATUS.OPEN,
          SOURCE_STATUS.PENDING,
        ].map((status) => ({
          columnKey: FEEDBACK_COLUMNS.TICKET_STATUS,
          filterKey: status,
          filterName: feedbackUtils.getSourceStatusLabel(status).value,
          filterOptionNode: (
            <IconTextProperty {...feedbackUtils.getSourceStatusLabel(status)} />
          ),
        })),
      },
      {
        columnKey: FEEDBACK_COLUMNS.STATUS,
        columnName: 'Analysis scope',
        filterable: true,
        filterList: [FEEDBACK_STATUS.FULL, FEEDBACK_STATUS.SENTIMENT_ONLY].map(
          (status) => ({
            columnKey: FEEDBACK_COLUMNS.STATUS,
            filterKey: status,
            filterName: feedbackUtils.getFeedbackStatusLabel(status),
            filterOptionNode: feedbackUtils.getFeedbackStatusLabel(status),
          }),
        ),
      },
      {
        columnKey: FEEDBACK_COLUMNS.AGENT,
        columnName: 'Name',
        filterable: true,
        filterList: [],
      },
      {
        columnKey: FEEDBACK_COLUMNS.LANGUAGE,
        columnName: 'Language',
        filterable: true,
        filterList: languageList.map((language) => ({
          columnKey: FEEDBACK_COLUMNS.LANGUAGE,
          filterKey: language.code,
          filterName: language.name,
          filterOptionNode: language.name,
        })),
      },
    ];
    return configs;
  }, [
    currentCategoryMap,
    languageList,
    tagMap,
    workspaceFieldOptions.data_origin_values,
  ]);

  return feedbackFilterConfig;
};

const useAccountFieldConfig = (): FilterOptionT[] => {
  const {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    countryOptions,
    statusOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
  } = useCompanyAttributes();

  const { fetchCompanyList } = useCompanyStore((state) => ({
    fetchCompanyList: state.fetchCompanyList,
  }));

  const accountFilterConfig: FilterOptionT[] = useMemo(() => {
    let configs: FilterOptionT[] = [
      {
        columnKey: ACCOUNT_COLUMNS.NAME_SEARCH,
        columnName: 'Name',
        searchable: true,
        asyncData: {
          fetchList: async (prev, currPage, targetPage, searchValue) => {
            const res = await fetchCompanyList({
              prevData: prev,
              page: targetPage,
              searchValue,
            });
            const list: PaginatedData<FilterOptionItemT>[] = res.map(
              (company) => ({
                columnKey: ACCOUNT_COLUMNS.NAME_SEARCH,
                filterKey: company.alias,
                filterName: company.name,
                filterOptionNode: company.name,
                paginate_token: company.paginate_token,
              }),
            );
            return {
              page: targetPage,
              hasNextPage: !!res[COMPANY_PAGE_SIZE - 1],
              list,
            };
          },
        },
      },
      {
        columnKey: ACCOUNT_COLUMNS.INDUSTRY,
        columnName: 'Industry',
        searchable: true,
        filterable: true,
        filterList: industryOptions.map((option) => ({
          columnKey: ACCOUNT_COLUMNS.INDUSTRY,
          filterKey: String(option.value),
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: ACCOUNT_COLUMNS.SIZE,
        columnName: 'Size',
        searchable: true,
        filterable: true,
        filterList: sizeOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.SIZE,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.PLAN,
        columnName: 'Plan',
        searchable: true,
        filterable: true,
        filterList: planOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.PLAN,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.RATING,
        columnName: 'Rating',
        searchable: true,
        filterable: true,
        filterList: ratingOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.RATING,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.TYPE,
        columnName: 'Type',
        searchable: true,
        filterable: true,
        filterList: accountTypeOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.TYPE,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.STAGE,
        columnName: 'Stage',
        searchable: true,
        filterable: true,
        filterList: accountStageOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.STAGE,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
        columnName: 'Primary Usecase',
        searchable: true,
        filterable: true,
        filterList: accountPrimaryUsecseOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.COUNTRY,
        columnName: 'Country',
        searchable: true,
        filterable: true,
        filterList: countryOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.COUNTRY,
            filterKey: String(option.value),
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
        columnName: 'Active status',
        filterable: true,
        filterList: statusOptions.map((option) => ({
          columnKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: ACCOUNT_COLUMNS.AGENT,
        columnName: 'Agent',
        filterable: true,
        filterList: [],
      },
    ];
    return configs;
  }, [
    accountPrimaryUsecseOptions,
    accountStageOptions,
    accountTypeOptions,
    countryOptions,
    fetchCompanyList,
    industryOptions,
    planOptions,
    ratingOptions,
    sizeOptions,
    statusOptions,
  ]);

  return accountFilterConfig;
};

const useContactFieldConfig = (): FilterOptionT[] => {
  const regionMap = useLocaleStore((state) => state.regionMap);
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );
  const contactFilterConfig: FilterOptionT[] = useMemo(() => {
    let configs: FilterOptionT[] = [
      {
        columnKey: CONTACT_COLUMNS.NAME_SEARCH,
        columnName: 'Name',
        searchable: true,
        filterable: true,
        asyncData: {
          fetchList: async (
            prevData: PaginatedData<FilterOptionItemT>[],
            currPage,
            targetPage,
            searchValue,
            acc,
          ) => {
            const prev = prevData.map((d) => ({
              alias: d.filterKey as string,
              name: d.filterName,
              paginate_token: d.paginate_token,
            }));

            const queries = getPaginationQueries<{
              alias: string;
              paginate_token?: string;
              name: string;
            }>({
              queries: {
                contact_name: searchValue || null,
              },
              prevData: prev,
              currPage,
              targetPage,
              cursorKey: 'paginate_token',
              sortby: 'name',
              acc,
            });
            const queryString = buildQuery(queries);
            const res = await service.contact.fetchCustomerList(queryString);
            const list: PaginatedData<FilterOptionItemT>[] = res.map(
              (customer) => ({
                columnKey: CONTACT_COLUMNS.NAME_SEARCH,
                filterKey: customer.alias,
                filterName: customer.name || '',
                filterOptionNode: customer.name || '',
                paginate_token: customer.paginate_token,
              }),
            );
            return {
              page: targetPage,
              hasNextPage: !!res[CUSTOMER_PAGE_SIZE - 1],
              list,
            };
          },
        },
      },
      {
        columnKey: CONTACT_COLUMNS.PLAN,
        columnName: 'Plan',
        searchable: true,
        filterable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.PLAN,
          workspaceFieldOptions.contact_plan_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.STAGE,
        columnName: 'Stage',
        searchable: true,
        filterable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.STAGE,
          workspaceFieldOptions.contact_stage_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.TYPE,
        columnName: 'Type',
        searchable: true,
        filterList: createOptionsFromValues(
          CONTACT_COLUMNS.TYPE,
          workspaceFieldOptions.contact_type_values,
        ),
      },
      {
        columnKey: CONTACT_COLUMNS.COUNTRY,
        columnName: 'Country',
        searchable: true,
        filterable: true,
        filterList: Object.keys(regionMap)
          .sort((a, b) =>
            regionMap[a].country.localeCompare(regionMap[b].country),
          )
          .map((key) => {
            return {
              columnKey: CONTACT_COLUMNS.COUNTRY,
              filterKey: key,
              filterName: regionMap[key].country,
              filterOptionNode: regionMap[key].country,
            };
          }),
      },
      {
        columnKey: CONTACT_COLUMNS.AGENT,
        columnName: 'Agent',
        filterable: true,
        filterList: [],
      },
    ];
    return configs;
  }, [
    regionMap,
    workspaceFieldOptions.contact_plan_values,
    workspaceFieldOptions.contact_stage_values,
    workspaceFieldOptions.contact_type_values,
  ]);

  return contactFilterConfig;
};

const useProductFieldConfig = (): FilterOptionT[] => {
  const productMap = useProductStore((state) => state.productMap);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productOptionMap = useProductStore((state) => state.productOptionMap);

  const productFilterConfig: FilterOptionT[] = useMemo(() => {
    let configs: FilterOptionT[] = [
      {
        columnKey: PRODUCT_COLUMNS.BRAND,
        columnName: 'Brand',
        searchable: true,
        filterable: true,
        filterList: Array.from(brandMap.values()).map((brand) => {
          return {
            columnKey: PRODUCT_COLUMNS.BRAND,
            filterKey: brand.id,
            filterName: brand.name,
            filterOptionNode: brand.name,
          };
        }),
      },
      {
        columnKey: PRODUCT_COLUMNS.LINE,
        columnName: 'Line',
        searchable: true,
        filterable: true,
        filterList: Array.from(lineMap.values()).map((line) => {
          return {
            columnKey: PRODUCT_COLUMNS.LINE,
            filterKey: line.id,
            filterName: line.name,
            filterOptionNode: line.name,
          };
        }),
      },
      {
        columnKey: PRODUCT_COLUMNS.PRODUCT,
        columnName: 'Product',
        searchable: true,
        filterable: true,
        filterList: Array.from(productMap.values()).map((product) => {
          return {
            columnKey: PRODUCT_COLUMNS.PRODUCT,
            filterKey: product.product_id,
            filterName: product.product_name || '',
            filterOptionNode: product.product_name || '',
          };
        }),
      },
      {
        columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
        columnName: 'Product Option',
        searchable: true,
        filterable: true,
        filterList: Array.from(productOptionMap.values()).map(
          (productOption) => {
            return {
              columnKey: PRODUCT_COLUMNS.PRODUCT_OPTION,
              filterKey: productOption.id,
              filterName: productOption.option_name,
              filterOptionNode: productOption.option_name,
            };
          },
        ),
      },
    ];
    return configs;
  }, [brandMap, lineMap, productMap, productOptionMap]);
  return productFilterConfig;
};

const useDemographicFieldConfig = (): FilterOptionT[] => {
  const regionList = useFeedbackRegionList();
  const demographicFilterConfig: FilterOptionT[] = useMemo(() => {
    let configs: FilterOptionT[] = [
      {
        columnKey: DEMOGRAPHIC_COLUMNS.GENDER,
        columnName: 'Gender',
        filterable: true,
        searchable: true,
        filterList: Array.from(genderList.entries()).map(([key, value]) => ({
          columnKey: DEMOGRAPHIC_COLUMNS.GENDER,
          filterKey: key,
          filterName: value,
          filterOptionNode: value,
        })),
      },
      {
        columnKey: DEMOGRAPHIC_COLUMNS.AGE,
        columnName: 'Age',
        filterable: true,
        searchable: true,
        filterList: Array.from(ageGroupList.entries()).map(([key, value]) => ({
          columnKey: DEMOGRAPHIC_COLUMNS.AGE,
          filterKey: key,
          filterName: value,
          filterOptionNode: value,
        })),
      },
      {
        columnKey: DEMOGRAPHIC_COLUMNS.RACE,
        columnName: 'Race',
        filterable: true,
        searchable: true,
        filterList: Object.values(RACE).map((value) => ({
          columnKey: DEMOGRAPHIC_COLUMNS.RACE,
          filterKey: value,
          filterName: value,
          filterOptionNode: value,
        })),
      },
      {
        columnKey: DEMOGRAPHIC_COLUMNS.LOCATION,
        columnName: 'Location',
        filterable: true,
        searchable: true,
        filterList: regionList.map((region) => ({
          columnKey: DEMOGRAPHIC_COLUMNS.LOCATION,
          filterKey: region.code,
          filterName: region.country,
          filterOptionNode: region.country,
        })),
      },
    ];
    return configs;
  }, [regionList]);
  return demographicFilterConfig;
};
// const useAgentFieldConfig = (): FilterOptionT[] => {
//   const fetchAgentList = useAgentStore((state) => state.fetchAgentList);
//   const { role_values, team_values } = useWorkspaceStore(
//     (state) => state.workspaceFieldOptions,
//   );

//   const agentFilterConfig: FilterOptionT[] = useMemo(() => {
//     let configs: FilterOptionT[] = [
//       {
//         columnKey: AGENT_COLUMNS.ALIAS,
//         columnName: 'Name',
//         filterable: true,
//         searchable: true,
//         asyncData: {
//           fetchList: async () => {
//             const res = await fetchAgentList();
//             const list: PaginatedData<FilterOptionItemT>[] = res.map(
//               (agent) => ({
//                 columnKey: AGENT_COLUMNS.ALIAS,
//                 filterKey: agent.alias,
//                 filterName: agent.name,
//                 filterOptionNode: (
//                   <Flex g="6px" a="center">
//                     <MainText ellipsis style={{ maxWidth: 100 }}>
//                       {agent.name}
//                     </MainText>
//                     <MainText dim ellipsis style={{ maxWidth: 150 }}>
//                       {agent.email}
//                     </MainText>
//                   </Flex>
//                 ),
//                 paginate_token: agent.alias,
//               }),
//             );
//             return {
//               page: 1,
//               hasNextPage: false,
//               list,
//             };
//           },
//         },
//       },
//       {
//         columnKey: AGENT_COLUMNS.ROLE,
//         columnName: 'Role',
//         filterable: true,
//         searchable: true,
//         filterList: createOptionsFromValues(AGENT_COLUMNS.ROLE, role_values),
//       },
//       {
//         columnKey: AGENT_COLUMNS.TEAM,
//         columnName: 'Primary Usecase',
//         filterable: true,
//         searchable: true,
//         filterList: createOptionsFromValues(AGENT_COLUMNS.TEAM, team_values),
//       },
//     ];

//     return configs;
//   }, [fetchAgentList, role_values, team_values]);

//   return agentFilterConfig;
// };

const mapField = (
  field: CHART_FIELD,
  callback?: (field: CHART_FIELD) => void,
  replaceFeedbackTerminology?: (text: string) => string,
) => {
  return {
    label: replaceFeedbackTerminology
      ? replaceFeedbackTerminology(getChartFieldLabel(field))
      : getChartFieldLabel(field),
    onClick: () => callback?.(field),
    key: field,
  };
};

export const getFieldMenu = (
  callback?: (field: CHART_FIELD) => void,
  replaceLabelText?: (text: string) => string,
) => {
  const menus: MenuItemProps[] = [
    {
      ...mapField(CHART_FIELD.FEEDBACK_ALL, callback, replaceLabelText),
      icon: <CircleDashed className="size-4" />,
    },
    {
      ...mapField(CHART_FIELD.TAXONOMY_TOPIC, callback, replaceLabelText),
      icon: <Blocks className="size-4" />,
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_TAG, callback, replaceLabelText),
      icon: <TagIcon className="size-4" />,
    },
    {
      label: 'Product info',
      icon: <Package className="size-4" />,
      subMenus: [
        CHART_FIELD.PRODUCT_BRAND,
        CHART_FIELD.PRODUCT_LINE,
        CHART_FIELD.PRODUCT_PRODUCT,
        CHART_FIELD.PRODUCT_PRODUCT_OPTION,
      ].map((field) => ({
        ...mapField(field, callback, replaceLabelText),
      })),
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_CATEGORY, callback),
      icon: <Archive className="size-4" />,
    },

    {
      label: 'Source',
      icon: <Workflow className="size-4" />,
      key: 'legacy-source',
      subMenus: [
        {
          ...mapField(CHART_FIELD.FEEDBACK_SOURCE, callback),
        },
        {
          ...mapField(CHART_FIELD.FEEDBACK_DATA_ORIGIN, callback),
        },
      ],
    },

    {
      label: 'Score',
      icon: <CircleStar className="size-4" />,
      subMenus: [
        CHART_FIELD.FEEDBACK_RATING_SCORE,
        CHART_FIELD.FEEDBACK_NPS_SCORE,
        CHART_FIELD.FEEDBACK_CES_SCORE,
        CHART_FIELD.FEEDBACK_CSAT,
      ].map((field) => ({
        ...mapField(field, callback),
      })),
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_AGENT, callback),
      icon: <Headset className="size-4" />,
    },

    {
      ...mapField(CHART_FIELD.FEEDBACK_LANGUAGE, callback),
      icon: <Globe className="size-4" />,
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_AD_PREDICTION, callback),
      icon: <ScanSearch className="size-4" />,
    },
    {
      label: 'Sentiment',
      icon: <Heart className="size-4" />,
      subMenus: [
        {
          ...mapField(CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL, callback),
        },
        {
          ...mapField(CHART_FIELD.FEEDBACK_SENTIMENT, callback),
        },
      ],
    },

    {
      ...mapField(CHART_FIELD.FEEDBACK_KEYWORD, callback),
      icon: <Hash className="size-4" />,
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_HASHTAG, callback),
      icon: <Hash className="size-4" />,
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_MENTION, callback),
      icon: <AtSign className="size-4" />,
    },
    {
      label: 'Demographic',
      icon: <BookUser className="size-4" />,
      subMenus: [
        CHART_FIELD.DEMOGRAPHIC_GENDER,
        CHART_FIELD.DEMOGRAPHIC_AGE,
        CHART_FIELD.DEMOGRAPHIC_RACE,
        CHART_FIELD.DEMOGRAPHIC_LOCATION,
      ].map((field) => ({
        ...mapField(field, callback),
      })),
    },
    {
      ...mapField(CHART_FIELD.FEEDBACK_REMARKS, callback),
      icon: <Asterisk className="size-4" />,
    },
    {
      type: MenuItemType.DIVIDER,
      label: 'divider',
    },
    {
      label: 'Contact',
      icon: <CircleUserRound className="size-4" />,
      subMenus: [
        CHART_FIELD.CONTACT_NAME,
        CHART_FIELD.CONTACT_PLAN,
        CHART_FIELD.CONTACT_STAGE,
        CHART_FIELD.CONTACT_TYPE,
        CHART_FIELD.CONTACT_COUNTRY,
        CHART_FIELD.CONTACT_AGENT,
      ].map((field) => ({
        ...mapField(field, callback),
      })),
    },
    {
      label: 'Account',
      icon: <Building className="size-4" />,
      subMenus: [
        CHART_FIELD.ACCOUNT_NAME,
        CHART_FIELD.ACCOUNT_INDUSTRY,
        CHART_FIELD.ACCOUNT_COUNTRY,
        CHART_FIELD.ACCOUNT_RATING,
        CHART_FIELD.ACCOUNT_SIZE,
        CHART_FIELD.ACCOUNT_PLAN,
        CHART_FIELD.ACCOUNT_TYPE,
        CHART_FIELD.ACCOUNT_USECASE,
        CHART_FIELD.ACCOUNT_STAGE,
        CHART_FIELD.ACCOUNT_ACTIVE_STATUS,
        CHART_FIELD.ACCOUNT_AGENT,
      ].map((field) => ({
        ...mapField(field, callback),
      })),
    },
  ];
  return menus;
};

const useFieldConfig = () => {
  const feedbackFieldConfig = useFeedbackFieldConfig();
  const accountFieldConfig = useAccountFieldConfig();
  const contactFieldConfig = useContactFieldConfig();
  const productFieldConfig = useProductFieldConfig();
  const demographicFieldConfig = useDemographicFieldConfig();

  const configs: FilterOptionT[] = useMemo(() => {
    return [
      ...feedbackFieldConfig,
      ...accountFieldConfig,
      ...contactFieldConfig,
      ...productFieldConfig,
      ...demographicFieldConfig,
    ];
  }, [
    accountFieldConfig,
    contactFieldConfig,
    feedbackFieldConfig,
    productFieldConfig,
    demographicFieldConfig,
  ]);

  return configs;
};

export const getParentFieldName = (field: CHART_FIELD): string | null => {
  const fieldMenu = getFieldMenu();

  for (const menu of fieldMenu) {
    if (menu.key === field) {
      return null;
    }
    if (menu.subMenus) {
      const subMenu = menu.subMenus.find((m) => m.key === field);
      if (subMenu) {
        return menu.label;
      }
    }
  }
  throw new Error(
    `[getParentFieldName] Field label not found for field: ${field}`,
  );
};

export default useFieldConfig;
